import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  Tabs,
  Tab,
  LinearProgress,
  Avatar,
  IconButton,
  Tooltip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Snackbar,
} from '@mui/material';
import {
  AccountBalance,
  TrendingUp,
  Schedule,
  AttachMoney,
  Assessment,
  CalendarToday,
  Notifications,
  CheckCircle,
  Warning,
  Error,
  Info,
  Download,
  Refresh,
  Visibility,
  Send,
  Close,
} from '@mui/icons-material';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  LineChart,
  Line,
} from 'recharts';
import { supabase } from '../../supabaseClient';
import { useTranslation } from 'react-i18next';
import { useDarkMode, getClozedStyles } from '../../hooks/useDarkMode';
import { useAuth } from '../../components/AuthProvider';

interface SettlementSummary {
  totalAmount: number;
  pendingAmount: number;
  completedAmount: number;
  brandCount: number;
  creatorCount: number;
  pendingCount: number;
  completedCount: number;
  totalAffiliateAmount: number;
}

interface WeeklySettlement {
  id: string;
  seller_id: string;
  settlement_type: 'sales' | 'affiliate';
  week_number: number;
  year: number;
  period_start_date: string;
  period_end_date: string;
  gross_sales: number;
  refund_amount: number;
  net_sales: number;
  commission_rate: number;
  commission_amount: number;
  final_amount: number;
  actual_payment_amount: number;
  carried_forward_amount: number;
  status: 'pending' | 'processing' | 'completed' | 'cancelled';
  due_date: string;
  seller_name?: string;
}

interface SettlementSchedule {
  id: string;
  brandName: string;
  period: string;
  amount: number;
  dueDate: string;
  status: 'upcoming' | 'due' | 'overdue';
}


interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

export default function Settlement() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isDarkMode = useDarkMode();
  const styles = getClozedStyles(isDarkMode);
  const [tabValue, setTabValue] = useState(0);
  const [periodFilter, setPeriodFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState<'all' | 'sales' | 'affiliate'>('all');
  const [summary, setSummary] = useState<SettlementSummary>({
    totalAmount: 0,
    pendingAmount: 0,
    completedAmount: 0,
    brandCount: 0,
    creatorCount: 0,
    pendingCount: 0,
    completedCount: 0,
    totalAffiliateAmount: 0,
  });
  const [settlements, setSettlements] = useState<WeeklySettlement[]>([]);
  const [schedule, setSchedule] = useState<SettlementSchedule[]>([]);
  const [settlementStatusData, setSettlementStatusData] = useState([
    { name: t('settlement.completed'), value: 0, color: '#4caf50' },
    { name: t('settlement.processing'), value: 0, color: '#ff9800' },
    { name: t('settlement.pending'), value: 0, color: '#2196f3' },
  ]);
  const [monthlyTrendData, setMonthlyTrendData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedSettlement, setSelectedSettlement] = useState<WeeklySettlement | null>(null);
  const [settlementDetails, setSettlementDetails] = useState<any[]>([]);
  const [processingSettlement, setProcessingSettlement] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' | 'warning' });

  // 데이터 로드
  useEffect(() => {
    loadSettlementData();
  }, [user]);

  const loadSettlementData = async () => {
    if (!user) {
      console.error('User not authenticated');
      return;
    }

    try {
      setLoading(true);

      const { data: profile } = await supabase
        .from('profiles')
        .select('role')
        .eq('id', user.id)
        .single();

      if (profile?.role !== 'admin') {
        console.error('User is not admin');
        return;
      }

      // 주간 정산 데이터 조회
      const { data: settlementsData, error: settlementsError } = await supabase
        .from('weekly_settlements')
        .select(`
          *,
          seller:seller_id (
            store_name
          )
        `)
        .order('year', { ascending: false })
        .order('week_number', { ascending: false });

      if (settlementsError) {
        console.error('Error loading settlements:', settlementsError);
        return;
      }

      // 셀러명 추가
      const formattedSettlements: WeeklySettlement[] = (settlementsData || []).map(s => ({
        ...s,
        seller_name: s.seller?.store_name || t('settlement.unknownSeller')
      }));

      setSettlements(formattedSettlements);

      // 판매 정산 관련
      const salesSettlements = formattedSettlements.filter(s => s.settlement_type === 'sales');
      const totalSales = salesSettlements.reduce((sum, s) => sum + s.final_amount, 0);
      const totalSalesGross = salesSettlements.reduce((sum, s) => sum + s.gross_sales, 0);

      // 어필리에이트 정산 관련
      const affiliateSettlements = formattedSettlements.filter(s => s.settlement_type === 'affiliate');
      const totalAffiliate = affiliateSettlements.reduce((sum, s) => sum + s.final_amount, 0);

      // 전체 통계
      const pendingSettlements = formattedSettlements.filter(s => s.status === 'pending');
      const pendingAmount = pendingSettlements.reduce((sum, s) => sum + s.final_amount, 0);
      const pendingCount = pendingSettlements.length;

      const completedSettlements = formattedSettlements.filter(s => s.status === 'completed');
      const completedAmount = completedSettlements.reduce((sum, s) => sum + s.final_amount, 0);
      const completedCount = completedSettlements.length;

      const processingCount = formattedSettlements.filter(s => s.status === 'processing').length;

      // 브랜드 및 크리에이터 수 계산
      const uniqueBrands = new Set(salesSettlements.map(s => s.seller_id));
      const uniqueCreators = new Set(affiliateSettlements.map(s => s.seller_id));

      // 주간 트렌드 데이터
      const weeklyData: { [key: string]: number } = {};
      formattedSettlements.forEach(s => {
        const weekKey = `${s.year}Y${s.week_number}W`;
        weeklyData[weekKey] = (weeklyData[weekKey] || 0) + s.final_amount;
      });

      const weeklyTrendArray = Object.entries(weeklyData)
        .map(([week, amount]) => ({ month: week, amount }))
        .slice(0, 6)
        .reverse();

      // 상태별 업데이트
      setSettlementStatusData([
        { name: t('settlement.completed'), value: completedCount, color: '#4caf50' },
        { name: t('settlement.processing'), value: processingCount, color: '#ff9800' },
        { name: t('settlement.pending'), value: pendingCount, color: '#2196f3' },
      ]);

      setMonthlyTrendData(weeklyTrendArray);

      // 정산 일정 데이터
      const now = new Date();
      const scheduleData = formattedSettlements.slice(0, 5).map((settlement, index) => {
        const dueDate = new Date(settlement.due_date);
        const isOverdue = dueDate < now && settlement.status !== 'completed';
        const isDue = dueDate.toDateString() === now.toDateString();

        return {
          id: settlement.id,
          brandName: settlement.seller_name || '',
          period: `${settlement.year}-W${settlement.week_number}`,
          amount: settlement.final_amount,
          dueDate: settlement.due_date,
          status: isOverdue ? 'overdue' : isDue ? 'due' : 'upcoming' as 'upcoming' | 'due' | 'overdue'
        };
      });

      setSchedule(scheduleData);

      // 요약 데이터 업데이트
      setSummary({
        totalAmount: totalSalesGross, // 총 판매액 (수수료 전)
        pendingAmount,
        completedAmount,
        brandCount: uniqueBrands.size,
        creatorCount: uniqueCreators.size,
        pendingCount,
        completedCount,
        totalAffiliateAmount: totalAffiliate,
      });

    } catch (error) {
      console.error('Error loading settlement data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'success';
      case 'processing': return 'warning';
      case 'pending': return 'info';
      default: return 'default';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'completed': return t('settlement.completed');
      case 'processing': return t('settlement.processing');
      case 'pending': return t('settlement.pending');
      default: return status;
    }
  };

  const getScheduleStatusColor = (status: string) => {
    switch (status) {
      case 'upcoming': return 'info';
      case 'due': return 'warning';
      case 'overdue': return 'error';
      default: return 'default';
    }
  };

  const getScheduleStatusText = (status: string) => {
    switch (status) {
      case 'upcoming': return t('settlement.upcoming');
      case 'due': return t('settlement.due');
      case 'overdue': return t('settlement.overdue');
      default: return status;
    }
  };

  const handleProcessSettlement = async (settlementId: string) => {
    if (!user) {
      setSnackbar({ open: true, message: t('settlement.authRequired'), severity: 'error' });
      return;
    }

    try {
      setProcessingSettlement(settlementId);

      // process_settlement 함수 호출
      const { data, error } = await supabase.rpc('process_settlement', {
        p_settlement_id: settlementId,
        p_admin_id: user.id
      });

      if (error) {
        console.error('Error processing settlement:', error);
        setSnackbar({ open: true, message: t('settlement.processFailed', { message: error.message }), severity: 'error' });
        return;
      }

      // 성공 메시지 표시
      const result = data as any;
      const severity = result.is_carry_forward ? 'warning' : 'success';
      setSnackbar({
        open: true,
        message: result.message,
        severity
      });

      // 데이터 새로고침
      await loadSettlementData();
    } catch (error) {
      console.error('Error processing settlement:', error);
      setSnackbar({ open: true, message: t('settlement.processError'), severity: 'error' });
    } finally {
      setProcessingSettlement(null);
    }
  };

  const handleViewDetail = async (settlement: WeeklySettlement) => {
    setSelectedSettlement(settlement);
    setSettlementDetails([]);

    try {
      if (settlement.settlement_type === 'sales') {
        // 판매 정산 상세 조회
        const { data: salesItems, error } = await supabase
          .from('weekly_settlement_sales_items')
          .select('*')
          .eq('settlement_id', settlement.id)
          .order('order_date', { ascending: false });

        if (error) {
          console.error('Error loading sales items:', error);
          return;
        }

        // 환불 내역도 조회 - 해당 정산에 적용된 환불만 조회
        const { data: refundItems, error: refundError } = await supabase
          .from('weekly_settlement_refunds')
          .select(`
            *,
            order_items:order_item_id (
              product_name,
              product_price,
              quantity,
              total_price
            )
          `)
          .eq('applied_settlement_id', settlement.id)
          .order('refund_date', { ascending: false });

        if (refundError) {
          console.error('Error loading refund items:', refundError);
        }

        // 판매 항목의 order_item_id 수집 (현재 상태 확인용)
        const orderItemIds = (salesItems || []).map(item => item.order_item_id).filter(Boolean);

        // 해당 주차 기간에 주문되었지만 정산에 포함되지 않은 주문 조회
        const periodEndPlusOne = new Date(new Date(settlement.period_end_date).getTime() + 24 * 60 * 60 * 1000).toISOString();

        const { data: excludedOrders, error: excludedError } = await supabase
          .from('order_items')
          .select(`
            id,
            product_name,
            product_price,
            quantity,
            total_price,
            item_delivery_status,
            created_at,
            order:order_id (
              id,
              payment_status,
              created_at
            ),
            product:product_id (
              seller_id
            )
          `)
          .in('item_delivery_status', ['cancelled', 'refunded', 'returned'])
          .order('created_at', { ascending: false })
          .limit(1000);

        if (excludedError) {
          console.error('Error loading excluded orders:', excludedError);
        }

        // 클라이언트에서 seller_id와 기간 필터링
        const periodStartDate = new Date(settlement.period_start_date);
        const periodEndDate = new Date(periodEndPlusOne);

        const filteredExcludedOrders = (excludedOrders || []).filter((item: any) => {
          if (!item.product || item.product.seller_id !== settlement.seller_id) {
            return false;
          }
          const orderDate = new Date(Array.isArray(item.order) ? item.order[0]?.created_at : item.order?.created_at);
          return orderDate >= periodStartDate && orderDate < periodEndDate;
        });

        // 해당 order_item들의 현재 상태 및 환불 정보 조회
        let currentOrderItemsStatus: any[] = [];
        let laterRefundRecords: any[] = [];

        if (orderItemIds.length > 0) {
          const { data: orderItemsData } = await supabase
            .from('order_items')
            .select('id, item_delivery_status')
            .in('id', orderItemIds);
          currentOrderItemsStatus = orderItemsData || [];

          // 정산 이후 환불된 레코드 조회 (applied_settlement_id가 현재 정산이 아닌 것)
          const { data: laterRefunds } = await supabase
            .from('weekly_settlement_refunds')
            .select(`
              *,
              order_items:order_item_id (
                product_name,
                product_price,
                quantity,
                total_price
              )
            `)
            .in('order_item_id', orderItemIds)
            .neq('applied_settlement_id', settlement.id)
            .order('refund_date', { ascending: false });

          laterRefundRecords = laterRefunds || [];
        }

        // 현재 상태 맵 생성
        const statusMap = new Map(
          currentOrderItemsStatus.map(item => [item.id, item.item_delivery_status])
        );

        // 판매 항목에 현재 상태 추가
        const salesItemsWithFlag = (salesItems || []).map(item => {
          const currentStatus = statusMap.get(item.order_item_id);
          const isLaterRefunded = currentStatus === 'refunded' || currentStatus === 'returned';

          return {
            ...item,
            is_refund: false,
            is_later_refunded: isLaterRefunded, // 정산 생성 이후 환불됨
            current_status: currentStatus,
          };
        });

        // 환불 항목을 형식에 맞게 변환 (정산에 적용된 환불)
        const refundItemsFormatted = (refundItems || []).map(item => ({
          product_name: item.order_items?.product_name || t('settlement.unknownProduct'),
          quantity: item.order_items?.quantity || 1,
          unit_price: item.order_items?.product_price || 0,
          total_amount: item.refund_amount,
          commission: 0,
          net_amount: item.refund_amount,
          order_date: item.refund_date,
          refund_date: item.refund_date,
          is_affiliate: false,
          affiliate_commission_rate: null,
          is_refund: true,
          is_later_refunded: false,
          is_reference_only: false, // 정산에 적용된 환불
        }));

        // 정산 이후 환불된 레코드 (참고용)
        const laterRefundsFormatted = laterRefundRecords.map(item => ({
          product_name: item.order_items?.product_name || t('settlement.unknownProduct'),
          quantity: item.order_items?.quantity || 1,
          unit_price: item.order_items?.product_price || 0,
          total_amount: item.refund_amount,
          commission: 0,
          net_amount: item.refund_amount,
          order_date: item.refund_date,
          refund_date: item.refund_date,
          is_affiliate: false,
          affiliate_commission_rate: null,
          is_refund: true,
          is_later_refunded: false,
          is_reference_only: true, // 참고용 (다른 주차 정산에 적용됨)
        }));

        // 제외된 주문 (같은 주에 취소/환불됨) - 참고용
        const excludedOrdersFormatted = filteredExcludedOrders.map((item: any) => ({
          product_name: item.product_name,
          quantity: item.quantity,
          unit_price: item.product_price,
          total_amount: item.total_price,
          commission: 0,
          net_amount: 0,
          order_date: (Array.isArray(item.order) ? item.order[0]?.created_at : item.order?.created_at) || item.created_at,
          is_affiliate: false,
          affiliate_commission_rate: null,
          is_refund: false,
          is_later_refunded: false,
          is_reference_only: true, // 참고용 (정산에서 제외됨)
          is_excluded: true, // 제외된 주문
          item_delivery_status: item.item_delivery_status,
        }));

        setSettlementDetails([...salesItemsWithFlag, ...refundItemsFormatted, ...laterRefundsFormatted, ...excludedOrdersFormatted]);
      } else {
        // 어필리에이트 정산 상세 조회
        const { data: affiliateItems, error } = await supabase
          .from('weekly_settlement_affiliate_items')
          .select('*')
          .eq('settlement_id', settlement.id)
          .order('earned_at', { ascending: false });

        if (error) {
          console.error('Error loading affiliate items:', error);
          return;
        }

        // 어필리에이트 환불 내역도 조회 - 해당 정산에 적용된 환불만 조회
        const { data: affiliateRefundItems, error: affiliateRefundError } = await supabase
          .from('weekly_settlement_affiliate_refunds')
          .select(`
            *,
            order_items:order_item_id (
              product_name,
              product_price,
              quantity,
              total_price,
              products:product_id (
                seller_profile:seller_id (
                  store_name
                )
              )
            )
          `)
          .eq('applied_settlement_id', settlement.id)
          .order('refund_date', { ascending: false });

        if (affiliateRefundError) {
          console.error('Error loading affiliate refund items:', affiliateRefundError);
        }

        const formattedItems = (affiliateItems || []).map(item => ({
          product_name: item.product_name,
          brand_name: item.brand_name || t('settlement.unknownBrand'),
          order_amount: item.order_amount,
          commission_rate: item.commission_rate,
          commission_amount: item.commission_amount,
          total_amount: item.order_amount,
          commission: item.commission_amount,
          earned_at: item.earned_at,
          is_refund: false,
        }));

        // 어필리에이트 환불 항목 변환
        const affiliateRefundFormatted = (affiliateRefundItems || []).map(item => ({
          product_name: item.order_items?.product_name || t('settlement.unknownProduct'),
          brand_name: item.order_items?.products?.seller_profile?.store_name || t('settlement.unknownBrand'),
          order_amount: item.order_items?.total_price || 0,
          commission_rate: 0,
          commission_amount: item.refund_amount,
          total_amount: item.order_items?.total_price || 0,
          commission: item.refund_amount,
          earned_at: item.refund_date,
          refund_date: item.refund_date,
          is_refund: true,
        }));

        setSettlementDetails([...formattedItems, ...affiliateRefundFormatted]);
      }

      setDetailDialogOpen(true);
    } catch (error) {
      console.error('Error loading settlement details:', error);
    }
  };

  return (
    <Box sx={{ ...styles.pageContainer, p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ ...styles.title, fontWeight: 'bold' }}>
          {t('settlement.dashboard')}
        </Typography>
        <Stack direction="row" spacing={2}>
          <Button variant="outlined" startIcon={<Download />}>
            {t('settlement.reportDownload')}
          </Button>
          <Button variant="contained" startIcon={<Refresh />} onClick={loadSettlementData}>
            {t('settlement.refresh')}
          </Button>
        </Stack>
      </Box>

      {/* 알림 */}
      <Alert severity="warning" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>{t('settlement.notice')}:</strong> {t('settlement.noticeContent')}
        </Typography>
      </Alert>

      {/* 요약 카드 */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={2.4}>
          <Card>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1}>
                <AttachMoney color="primary" fontSize="large" />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" color="text.secondary">{t('settlement.totalSalesBeforeFee')}</Typography>
                  <Typography variant="h5" color="primary">
                    ₩{summary.totalAmount.toLocaleString()}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={2.4}>
          <Card>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1}>
                <TrendingUp color="success" fontSize="large" />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" color="text.secondary">{t('settlement.creatorEarnings')}</Typography>
                  <Typography variant="h5" color="success.main">
                    ₩{summary.totalAffiliateAmount.toLocaleString()}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={2.4}>
          <Card>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Schedule color="warning" fontSize="large" />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" color="text.secondary">{t('settlement.pendingSettlement')}</Typography>
                  <Typography variant="h5" color="warning.main">
                    ₩{summary.pendingAmount.toLocaleString()}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {summary.pendingCount}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={2.4}>
          <Card>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1}>
                <CheckCircle color="success" fontSize="large" />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" color="text.secondary">{t('settlement.completedSettlement')}</Typography>
                  <Typography variant="h5" color="success.main">
                    ₩{summary.completedAmount.toLocaleString()}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {summary.completedCount}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={2.4}>
          <Card>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Assessment color="info" fontSize="large" />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" color="text.secondary">{t('settlement.brandCreatorCount')}</Typography>
                  <Typography variant="h5" color="info.main">
                    {summary.brandCount} / {summary.creatorCount}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* 차트 섹션 */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              {t('settlement.monthlyTrend')}
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={monthlyTrendData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <RechartsTooltip formatter={(value) => [`₩${Number(value).toLocaleString()}`, t('settlement.settlementAmount')]} />
                <Line type="monotone" dataKey="amount" stroke="#1976d2" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              {t('settlement.statusDistribution')}
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={settlementStatusData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {settlementStatusData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <RechartsTooltip />
              </PieChart>
            </ResponsiveContainer>
            <Stack spacing={1} sx={{ mt: 2 }}>
              {settlementStatusData.map((item, index) => (
                <Stack key={index} direction="row" alignItems="center" spacing={1}>
                  <Box sx={{ width: 12, height: 12, bgcolor: item.color, borderRadius: 1 }} />
                  <Typography variant="body2" sx={{ flex: 1 }}>
                    {item.name}
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {item.value}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Paper>
        </Grid>
      </Grid>

      {/* 탭 섹션 */}
      <Paper sx={{ p: 3 }}>
        <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)} sx={{ mb: 3 }}>
          <Tab label={t('settlement.byBrandStatus')} />
          <Tab label={t('settlement.schedule')} />
          <Tab label={t('settlement.categoryAnalysis')} />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          {/* 주간 정산 현황 */}
          <Box sx={{ mb: 2 }}>
            <Stack direction="row" spacing={2}>
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel>{t('settlement.settlementType')}</InputLabel>
                <Select
                  value={typeFilter}
                  label={t('settlement.settlementType')}
                  onChange={(e) => setTypeFilter(e.target.value as any)}
                >
                  <MenuItem value="all">{t('settlement.allType')}</MenuItem>
                  <MenuItem value="sales">{t('settlement.salesType')}</MenuItem>
                  <MenuItem value="affiliate">{t('settlement.affiliateType')}</MenuItem>
                </Select>
              </FormControl>
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel>{t('common.status')}</InputLabel>
                <Select
                  value={periodFilter}
                  label={t('common.status')}
                  onChange={(e) => setPeriodFilter(e.target.value)}
                >
                  <MenuItem value="all">{t('settlement.allType')}</MenuItem>
                  <MenuItem value="pending">{t('settlement.pending')}</MenuItem>
                  <MenuItem value="processing">{t('settlement.processing')}</MenuItem>
                  <MenuItem value="completed">{t('settlement.completed')}</MenuItem>
                </Select>
              </FormControl>
            </Stack>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('settlement.settlementType')}</TableCell>
                  <TableCell>{t('settlement.sellerCreator')}</TableCell>
                  <TableCell>{t('settlement.week')}</TableCell>
                  <TableCell>{t('settlement.settlementPeriod')}</TableCell>
                  <TableCell align="right">{t('settlement.grossSales')}</TableCell>
                  <TableCell align="right">{t('settlement.refundDeduction')}</TableCell>
                  <TableCell align="right">{t('settlement.netSales')}</TableCell>
                  <TableCell align="right">{t('settlement.commission')}</TableCell>
                  <TableCell align="right">{t('settlement.finalAmount')}</TableCell>
                  <TableCell align="right">{t('settlement.actualPayment')}</TableCell>
                  <TableCell>{t('common.status')}</TableCell>
                  <TableCell align="center">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={12} align="center">
                      <Typography>{t('settlement.loadingData')}</Typography>
                    </TableCell>
                  </TableRow>
                ) : settlements.filter(s =>
                    (typeFilter === 'all' || s.settlement_type === typeFilter) &&
                    (periodFilter === 'all' || s.status === periodFilter)
                  ).length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={12} align="center">
                      <Typography>{t('settlement.noSettlementHistory')}</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  settlements
                    .filter(s =>
                      (typeFilter === 'all' || s.settlement_type === typeFilter) &&
                      (periodFilter === 'all' || s.status === periodFilter)
                    )
                    .map((settlement) => (
                      <TableRow key={settlement.id} hover>
                        <TableCell>
                          <Chip
                            label={settlement.settlement_type === 'sales' ? t('settlement.salesSettlementChip') : t('settlement.affiliateSettlementChip')}
                            color={settlement.settlement_type === 'sales' ? 'primary' : 'success'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold">
                            {settlement.seller_name}
                          </Typography>
                        </TableCell>
                        <TableCell>{settlement.week_number}{t('settlement.week')}</TableCell>
                        <TableCell>
                          {new Date(settlement.period_start_date).toLocaleDateString('ko-KR', { month: 'short', day: 'numeric' })} ~
                          {new Date(settlement.period_end_date).toLocaleDateString('ko-KR', { month: 'short', day: 'numeric' })}
                        </TableCell>
                        <TableCell align="right">
                          ₩{settlement.gross_sales.toLocaleString()}
                        </TableCell>
                        <TableCell align="right">
                          <Typography color="error">
                            ₩{settlement.refund_amount.toLocaleString()}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          ₩{settlement.net_sales.toLocaleString()}
                        </TableCell>
                        <TableCell align="right">
                          ₩{settlement.commission_amount.toLocaleString()}
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            variant="body2"
                            fontWeight="bold"
                            color={settlement.final_amount < 0 ? 'error' : 'primary'}
                          >
                            ₩{settlement.final_amount.toLocaleString()}
                          </Typography>
                          {settlement.final_amount < 0 && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              {t('settlement.carryOver')}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            variant="body2"
                            fontWeight="bold"
                            color="success.main"
                          >
                            ₩{settlement.actual_payment_amount?.toLocaleString() || '0'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={getStatusText(settlement.status)}
                            color={getStatusColor(settlement.status) as any}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Stack direction="row" spacing={1}>
                            <Tooltip title={t('settlement.viewDetail')}>
                              <IconButton size="small" onClick={() => handleViewDetail(settlement)}>
                                <Visibility />
                              </IconButton>
                            </Tooltip>
                            {(settlement.status === 'pending' || settlement.status === 'processing') && (
                              <Tooltip title={settlement.status === 'pending' ? t('settlement.approveToProcessing') : t('settlement.processingToCompleted')}>
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleProcessSettlement(settlement.id)}
                                  disabled={processingSettlement === settlement.id}
                                >
                                  <Send />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          {/* 정산 일정 */}
          <Typography variant="h6" gutterBottom>
            {t('settlement.scheduleManagement')}
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('settlement.brandName')}</TableCell>
                  <TableCell>{t('settlement.settlementPeriod')}</TableCell>
                  <TableCell align="right">{t('settlement.expectedAmount')}</TableCell>
                  <TableCell>{t('settlement.deadline')}</TableCell>
                  <TableCell>{t('common.status')}</TableCell>
                  <TableCell align="center">{t('settlement.notification')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {schedule.map((schedule) => (
                  <TableRow key={schedule.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {schedule.brandName}
                      </Typography>
                    </TableCell>
                    <TableCell>{schedule.period}</TableCell>
                    <TableCell align="right">
                      ₩{schedule.amount.toLocaleString()}
                    </TableCell>
                    <TableCell>{schedule.dueDate}</TableCell>
                    <TableCell>
                      <Chip
                        label={getScheduleStatusText(schedule.status)}
                        color={getScheduleStatusColor(schedule.status) as any}
                        size="small"
                        icon={
                          schedule.status === 'overdue' ? <Warning /> :
                          schedule.status === 'due' ? <Error /> : <CalendarToday />
                        }
                      />
                    </TableCell>
                    <TableCell align="center">
                      <IconButton size="small" color="info">
                        <Notifications />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          {/* 유형별 분석 */}
          <Typography variant="h6" gutterBottom>
            {t('settlement.typeAnalysis')}
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>{t('settlement.salesSettlementCard')}</Typography>
                  <Typography variant="h4" color="primary">
                    ₩{settlements.filter(s => s.settlement_type === 'sales').reduce((sum, s) => sum + s.final_amount, 0).toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {settlements.filter(s => s.settlement_type === 'sales').length}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>{t('settlement.affiliateSettlementCard')}</Typography>
                  <Typography variant="h4" color="success.main">
                    ₩{settlements.filter(s => s.settlement_type === 'affiliate').reduce((sum, s) => sum + s.final_amount, 0).toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {settlements.filter(s => s.settlement_type === 'affiliate').length}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>
      </Paper>

      {/* 상세 정보 다이얼로그 */}
      <Dialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        maxWidth="xl"
        fullWidth
        PaperProps={{ sx: { minWidth: '1200px' } }}
      >
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">{t('settlement.detailTitle')}</Typography>
            <IconButton onClick={() => setDetailDialogOpen(false)} size="small">
              <Close />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent>
          {selectedSettlement && (
            <>
              {/* 정산 기본 정보 */}
              <Paper sx={{ p: 2, mb: 3, bgcolor: '#f5f5f5' }}>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">{t('settlement.settlementTypeLabel')}</Typography>
                    <Chip
                      label={selectedSettlement.settlement_type === 'sales' ? t('settlement.salesSettlement') : t('settlement.affiliateSettlement')}
                      color={selectedSettlement.settlement_type === 'sales' ? 'primary' : 'success'}
                      size="small"
                      sx={{ mt: 0.5 }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">{t('settlement.sellerCreator')}</Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {selectedSettlement.seller_name || t('settlement.unknownSeller')}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">{t('settlement.settlementPeriodLabel')}</Typography>
                    <Typography variant="body1">
                      {t('settlement.weekNumber', { year: selectedSettlement.year, week: selectedSettlement.week_number })}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(selectedSettlement.period_start_date).toLocaleDateString()} ~ {new Date(selectedSettlement.period_end_date).toLocaleDateString()}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">{t('settlement.paymentScheduledDate')}</Typography>
                    <Typography variant="body1">
                      {new Date(selectedSettlement.due_date).toLocaleDateString()}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>

              <Divider sx={{ my: 2 }} />

              {/* 금액 정보 */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6}>
                  <Stack spacing={1}>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">{t('settlement.grossSalesLabel')}</Typography>
                      <Typography variant="body2">₩{selectedSettlement.gross_sales.toLocaleString()}</Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">{t('settlement.refundDeductionLabel')}</Typography>
                      <Typography variant="body2" color="error">-₩{selectedSettlement.refund_amount.toLocaleString()}</Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">{t('settlement.netSalesLabel')}</Typography>
                      <Typography variant="body2">₩{selectedSettlement.net_sales.toLocaleString()}</Typography>
                    </Stack>
                    {selectedSettlement.carried_forward_amount !== 0 && (
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="body2" color="text.secondary">{t('settlement.carryOverAmount')}</Typography>
                        <Typography variant="body2" color={selectedSettlement.carried_forward_amount < 0 ? 'error' : 'primary'}>
                          ₩{selectedSettlement.carried_forward_amount.toLocaleString()}
                        </Typography>
                      </Stack>
                    )}
                  </Stack>
                </Grid>
                <Grid item xs={6}>
                  <Stack spacing={1}>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">{t('settlement.commissionRate')}</Typography>
                      <Typography variant="body2">{selectedSettlement.commission_rate}%</Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">{t('settlement.commissionLabel')}</Typography>
                      <Typography variant="body2" color="error">-₩{selectedSettlement.commission_amount.toLocaleString()}</Typography>
                    </Stack>
                    <Divider />
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">{t('settlement.finalAmountLabel')}</Typography>
                      <Typography variant="body2" color={selectedSettlement.final_amount < 0 ? 'error' : 'primary'}>
                        ₩{selectedSettlement.final_amount.toLocaleString()}
                      </Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body1" fontWeight="bold">{t('settlement.actualPaymentLabel')}</Typography>
                      <Typography variant="body1" fontWeight="bold" color="success.main">
                        ₩{selectedSettlement.actual_payment_amount?.toLocaleString() || '0'}
                      </Typography>
                    </Stack>
                    {selectedSettlement.final_amount < 0 && (
                      <Alert severity="warning" sx={{ mt: 1 }}>
                        {t('settlement.negativeSettlement', { amount: Math.abs(selectedSettlement.final_amount).toLocaleString() })}
                      </Alert>
                    )}
                  </Stack>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              {/* 상세 항목 테이블 */}
              <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
                {selectedSettlement.settlement_type === 'sales' ? t('settlement.salesDetailTitle') : t('settlement.affiliateEarningsTitle')}
              </Typography>
              <TableContainer sx={{ maxHeight: 400 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      {selectedSettlement.settlement_type === 'sales' ? (
                        <>
                          <TableCell>{t('settlement.productNameCol')}</TableCell>
                          <TableCell align="center">Affiliate</TableCell>
                          <TableCell align="right">{t('settlement.quantityCol')}</TableCell>
                          <TableCell align="right">{t('settlement.unitPrice')}</TableCell>
                          <TableCell align="right">{t('settlement.totalCol')}</TableCell>
                          <TableCell align="right">{t('settlement.platformFee')}</TableCell>
                          <TableCell align="right">{t('settlement.affiliateFee')}</TableCell>
                          <TableCell align="right">{t('settlement.paymentFee')}</TableCell>
                          <TableCell align="right">{t('settlement.settlementAmountCol')}</TableCell>
                          <TableCell>{t('settlement.orderDate')}</TableCell>
                        </>
                      ) : (
                        <>
                          <TableCell>{t('settlement.productNameCol')}</TableCell>
                          <TableCell>{t('settlement.brandCol')}</TableCell>
                          <TableCell align="right">{t('settlement.orderAmount')}</TableCell>
                          <TableCell align="right">{t('settlement.creatorCommissionRate')}</TableCell>
                          <TableCell align="right">{t('settlement.receivedCommission')}</TableCell>
                          <TableCell>{t('settlement.earningDate')}</TableCell>
                        </>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {settlementDetails.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={selectedSettlement.settlement_type === 'sales' ? 10 : 6} align="center">
                          <Typography variant="body2" color="text.secondary">
                            {t('settlement.noDetailHistory')}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : selectedSettlement.settlement_type === 'sales' ? (
                      settlementDetails.map((item: any, index: number) => {
                        const isAffiliate = item.is_affiliate || false;
                        const isRefund = item.is_refund || false;
                        const isLaterRefunded = item.is_later_refunded || false;
                        const isReferenceOnly = item.is_reference_only || false;
                        const isExcluded = item.is_excluded || false;
                        const baseCommissionRate = selectedSettlement?.commission_rate || 10;
                        const affiliateCommissionRate = item.affiliate_commission_rate || 0;
                        const paymentFee = item.total_amount * 0.036;
                        const affiliateCommission = isAffiliate ? item.total_amount * (affiliateCommissionRate / 100) : 0;

                        return (
                          <TableRow
                            key={index}
                            sx={isRefund && !isReferenceOnly ? {
                              backgroundColor: 'rgba(244, 67, 54, 0.08)',
                            } : isReferenceOnly ? {
                              backgroundColor: 'rgba(158, 158, 158, 0.08)',
                            } : isLaterRefunded ? {
                              backgroundColor: 'rgba(255, 152, 0, 0.08)',
                            } : {}}
                          >
                            <TableCell>
                              <Stack direction="row" spacing={1} alignItems="center">
                                {isRefund && !isReferenceOnly && (
                                  <Chip label={t('settlement.refundChip')} color="error" size="small" />
                                )}
                                {isReferenceOnly && !isExcluded && (
                                  <Chip label={t('settlement.referenceOtherWeek')} color="default" size="small" />
                                )}
                                {isExcluded && (
                                  <Chip
                                    label={t('settlement.referenceCancelled', { status: item.item_delivery_status === 'cancelled' ? t('settlement.cancelledStatus') : t('settlement.sameWeekRefund') })}
                                    color="default"
                                    size="small"
                                  />
                                )}
                                {isLaterRefunded && (
                                  <Chip label={t('settlement.laterRefunded')} color="warning" size="small" />
                                )}
                                <Typography>{item.product_name}</Typography>
                              </Stack>
                            </TableCell>
                            <TableCell align="center">
                              {isAffiliate ? (
                                <Chip label="Y" color="success" size="small" />
                              ) : (
                                <Chip label="N" color="default" size="small" variant="outlined" />
                              )}
                            </TableCell>
                            <TableCell align="right">
                              <Typography>
                                {isRefund ? `-${item.quantity}` : item.quantity}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography>
                                ₩{item.unit_price?.toLocaleString()}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography color={isRefund ? 'error' : 'inherit'}>
                                {isRefund ? '-' : ''}₩{item.total_amount?.toLocaleString()}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              {isRefund ? (
                                <Typography variant="body2" color="text.secondary">
                                  -
                                </Typography>
                              ) : (
                                <Stack direction="column" alignItems="flex-end">
                                  <Typography variant="body2">{baseCommissionRate}%</Typography>
                                  <Typography variant="body2" color="error">
                                    -₩{item.commission?.toLocaleString()}
                                  </Typography>
                                </Stack>
                              )}
                            </TableCell>
                            <TableCell align="right">
                              {isRefund ? (
                                <Typography variant="body2" color="text.secondary">
                                  -
                                </Typography>
                              ) : isAffiliate ? (
                                <Stack direction="column" alignItems="flex-end">
                                  <Typography variant="body2">{affiliateCommissionRate}%</Typography>
                                  <Typography variant="body2" color="error">
                                    -₩{Math.round(affiliateCommission).toLocaleString()}
                                  </Typography>
                                </Stack>
                              ) : (
                                <Typography variant="body2" color="text.secondary">-</Typography>
                              )}
                            </TableCell>
                            <TableCell align="right">
                              {isRefund ? (
                                <Typography variant="body2" color="text.secondary">
                                  -
                                </Typography>
                              ) : (
                                <Stack direction="column" alignItems="flex-end">
                                  <Typography variant="body2">3.6%</Typography>
                                  <Typography variant="body2" color="error">
                                    -₩{Math.round(paymentFee).toLocaleString()}
                                  </Typography>
                                </Stack>
                              )}
                            </TableCell>
                            <TableCell align="right">
                              <Typography
                                variant="body2"
                                fontWeight="bold"
                                color={isReferenceOnly ? 'text.secondary' : isRefund ? 'error' : 'inherit'}
                              >
                                {isReferenceOnly ? t('settlement.referenceAmount') : (
                                  <>
                                    {isRefund ? '-' : ''}₩{Math.abs(item.net_amount || 0).toLocaleString()}
                                  </>
                                )}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              {isRefund
                                ? (item.refund_date ? new Date(item.refund_date).toLocaleDateString() : '-')
                                : (item.order_date ? new Date(item.order_date).toLocaleDateString() : '-')
                              }
                            </TableCell>
                          </TableRow>
                        );
                      })
                    ) : (
                      settlementDetails.map((item: any, index: number) => {
                        const isRefund = item.is_refund || false;
                        return (
                          <TableRow
                            key={index}
                            sx={isRefund ? {
                              backgroundColor: 'rgba(244, 67, 54, 0.08)',
                            } : {}}
                          >
                            <TableCell>
                              <Stack direction="row" spacing={1} alignItems="center">
                                {isRefund && (
                                  <Chip label={t('settlement.refundChip')} color="error" size="small" />
                                )}
                                <Typography>{item.product_name}</Typography>
                              </Stack>
                            </TableCell>
                            <TableCell>{item.brand_name || item.brand?.store_name || t('settlement.unknown')}</TableCell>
                            <TableCell align="right">
                              <Typography color={isRefund ? 'error' : 'inherit'}>
                                {isRefund ? '-' : ''}₩{item.order_amount?.toLocaleString()}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">{item.commission_rate}%</TableCell>
                            <TableCell align="right">
                              <Typography color={isRefund ? 'error' : 'inherit'}>
                                {isRefund ? '-' : ''}₩{item.commission_amount?.toLocaleString()}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              {isRefund
                                ? (item.refund_date ? new Date(item.refund_date).toLocaleDateString() : '-')
                                : (item.earned_at ? new Date(item.earned_at).toLocaleDateString() : '-')
                              }
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialogOpen(false)}>{t("common.close")}</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}